/**
 * Repair and rearm tab and the Lease Office tab (spec §7.3, §9.2).
 */
import { useState, type ReactNode } from 'react'
import { FocusButton, Stepper, useFocusable } from '../focus'
import { formatCr } from '../format'
import { G, leaseItems, REPAIR_ITEMS } from '../models/layout'
import type { LeaseModel } from '../models/lease'
import { partialRearmLabel, partialRepairLabel, platingRepair, platingRepairLabel, rearmRateNote, repairRateNote, weaponPartsRearm, weaponPartsRearmLabel, type PlatingQuote, type RepairModel } from '../models/repair'
import { clampQuantity, stepQuantity } from '../models/stepper'
import type { ScreenProps } from './common'

/** A focused stepper row with an action button (partial repair, rearm, pay extra). */
function StepperRow(p: {
  group: string
  index: number
  value: number
  valueLabel: string
  label: string
  step: number
  max: number
  onChange: (v: number) => void
  onActivate: () => void
  testId: string
  stepperLabel: string
}): ReactNode {
  const can = p.max > 0 && p.value > 0
  const adjust = (d: 1 | -1): void => p.onChange(stepQuantity(p.value, d, p.step, p.max))
  const activate = (): void => {
    if (can) p.onActivate()
  }
  const f = useFocusable(p.group, p.index, { activate, adjust })
  return (
    <div className="ui-row ui-row--stepper" {...f.props}>
      <Stepper value={p.valueLabel} onStep={adjust} disabled={p.max <= 0} label={p.stepperLabel} />
      <button type="button" tabIndex={-1} className="ui-btn ui-btn--small" data-testid={p.testId} disabled={!can} onClick={activate}>
        {p.label}
      </button>
    </div>
  )
}

export function RepairTab(p: ScreenProps & { model: RepairModel }): ReactNode {
  const { model, ctl } = p
  const steps = ctl.cfg.steps
  const [points, setPoints] = useState<number>(steps.repairPoints)
  const [count, setCount] = useState<number>(steps.torpedoes)
  const pts = clampQuantity(points, steps.repairPoints, model.missing)
  const n = clampQuantity(count, steps.torpedoes, model.missingTorpedoes)
  // M8 SNK-3b: under the strategic economy a repair is paid in hull plating (hold first) at the station's price.
  const quoteGood = ctl.actions.quoteGood
  const quote: PlatingQuote | undefined = quoteGood === undefined ? undefined : (t) => quoteGood('hull_plating', t, 'buy')
  // K12: at a linked station that stocks none (refinery, habitat) the hold pays and the sub-line says so.
  const allPlatingRow = platingRepair(model, model.missing, quote, 'Repair all')
  const allPlating = allPlatingRow?.label ?? null
  const partPlating = platingRepairLabel(model, pts, quote)
  // M8 SNK-5: under the strategic economy a torpedo is paid in weapon parts (hold first) at the station's price.
  const partsQuote: PlatingQuote | undefined = quoteGood === undefined ? undefined : (t) => quoteGood('weapon_parts', t, 'buy')
  const allPartsRow = weaponPartsRearm(model, model.missingTorpedoes, partsQuote, 'Rearm all')
  const allParts = allPartsRow?.label ?? null
  const partParts = weaponPartsRearmLabel(model, n, partsQuote)
  return (
    <div className="ui-tab ui-repair">
      <section className="ui-section">
        <h3 className="ui-section__title">Hull</h3>
        <p className="ui-figure">
          <span className="ui-figure__label">Hull</span> <span className="ui-figure__value">{model.hullLabel}</span>
          <span className="ui-row__sub"> {'·'} {repairRateNote(model, quote)}</span>
        </p>
        <FocusButton group={G.service} index={REPAIR_ITEMS.indexOf('repairAll')} testId="repair-all" className="ui-btn--wide" disabled={!model.canRepair} onActivate={() => ctl.run(() => ctl.actions.repair('all'))}>
          {allPlating ?? model.repairAllLabel}
        </FocusButton>
        <StepperRow
          group={G.service}
          index={REPAIR_ITEMS.indexOf('repairPartial')}
          value={pts}
          valueLabel={`${pts} hull`}
          label={partPlating ?? partialRepairLabel(ctl.data, pts)}
          step={steps.repairPoints}
          max={model.missing}
          onChange={setPoints}
          onActivate={() => ctl.run(() => ctl.actions.repair(pts))}
          testId="repair-partial"
          stepperLabel="hull points"
        />
      </section>
      <section className="ui-section">
        <h3 className="ui-section__title">Torpedoes</h3>
        <p className="ui-figure">
          <span className="ui-figure__label">Racks</span> <span className="ui-figure__value">{model.torpedoLabel}</span>
          <span className="ui-row__sub"> {'·'} {rearmRateNote(model, partsQuote)}</span>
        </p>
        <FocusButton group={G.service} index={REPAIR_ITEMS.indexOf('rearmAll')} testId="rearm-all" className="ui-btn--wide" disabled={!model.canRearm} onActivate={() => ctl.run(() => ctl.actions.rearm('all'))}>
          {allParts ?? model.rearmAllLabel}
        </FocusButton>
        <StepperRow
          group={G.service}
          index={REPAIR_ITEMS.indexOf('rearmPartial')}
          value={n}
          valueLabel={`${n}`}
          label={partParts ?? partialRearmLabel(ctl.data, n)}
          step={steps.torpedoes}
          max={model.missingTorpedoes}
          onChange={setCount}
          onActivate={() => ctl.run(() => ctl.actions.rearm(n))}
          testId="rearm-partial"
          stepperLabel="torpedoes"
        />
      </section>
    </div>
  )
}

function Figure(p: { label: string; value: string; tone?: 'warning' | 'danger' }): ReactNode {
  return (
    <div className="ui-figure" data-tone={p.tone}>
      <span className="ui-figure__label">{p.label}</span>
      <span className="ui-figure__value">{p.value}</span>
    </div>
  )
}

export function LeaseTab(p: ScreenProps & { model: LeaseModel }): ReactNode {
  const { model, ctl } = p
  const step = ctl.cfg.steps.payExtra
  const [amount, setAmount] = useState<number>(step)
  const pay = clampQuantity(amount, step, model.maxExtra)
  const items = leaseItems(model.owned)
  const tone = model.collections.severity === 'ok' ? undefined : model.collections.severity
  return (
    <div className="ui-tab ui-lease">
      <section className="ui-section ui-figures">
        <Figure label="Principal" value={model.owned ? 'Owned' : model.principal} />
        <Figure label="Rate" value={model.owned ? '—' : model.rate} />
        <Figure label="Payment" value={model.owned ? '—' : model.payment} />
        <Figure label="Next due" value={model.nextDue} />
        <Figure label="Collections" value={`${model.collections.severity === 'ok' ? '✓' : '⚠'} ${model.collections.label}`} tone={tone} />
        <Figure label="Missed payments" value={String(model.missed)} tone={model.missed > 0 ? 'warning' : undefined} />
        <Figure label="Arrears" value={model.arrears} />
        <Figure label="Late fees" value={model.fees} />
        {model.debt !== null ? <Figure label="Insurer debt" value={model.debt} tone="warning" /> : null}
      </section>
      <section className="ui-section">
        {items.map((it, i) => {
          switch (it) {
            case 'arrears':
              return (
                <FocusButton key={it} group={G.lease} index={i} testId="lease-pay-arrears" className="ui-btn--wide" disabled={!model.canPayArrears} onActivate={() => ctl.run(() => ctl.actions.payArrears())}>
                  {model.arrearsLabel}
                </FocusButton>
              )
            case 'extra':
              return (
                <StepperRow
                  key={it}
                  group={G.lease}
                  index={i}
                  value={pay}
                  valueLabel={formatCr(pay)}
                  label={pay > 0 ? `Pay extra ${formatCr(pay)}` : 'Pay extra'}
                  step={step}
                  max={model.maxExtra}
                  onChange={setAmount}
                  onActivate={() => ctl.run(() => ctl.actions.payExtra(pay))}
                  testId="lease-pay-extra"
                  stepperLabel="extra payment"
                />
              )
            case 'insurance':
              return (
                <FocusButton key={it} group={G.lease} index={i} testId="lease-insurance" className="ui-btn--wide ui-toggle" onActivate={() => ctl.run(() => ctl.actions.setInsurance(!model.insured))}>
                  <span className="ui-switch" data-on={model.insured ? '1' : '0'} aria-hidden="true" />
                  Insurance: {model.insured ? 'on' : 'off'} ({model.premium} per cycle, halves the rebuild fee)
                </FocusButton>
              )
          }
        })}
      </section>
      {model.recovery !== null ? (
        <p className="ui-seed" data-tone="danger" data-testid="lease-recovery">
          {model.recovery}
        </p>
      ) : null}
      {model.covenant !== null ? (
        <section className="ui-section" data-testid="lease-covenant">
          <h3 className="ui-section__title">{model.covenant.title}</h3>
          <p className="ui-seed">
            Read aloud at signing by {model.covenant.reading.speaker}: {'“'}
            {model.covenant.reading.text}
            {'”'}
          </p>
          <ul className="ui-seed__clauses">
            {model.covenant.clauses.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </section>
      ) : null}
      <section className="ui-section">
        <h3 className="ui-section__title">History</h3>
        {model.history.length === 0 ? <p className="ui-empty">No lease activity yet.</p> : null}
        <table className="ui-history">
          <tbody>
            {model.history.map((h, i) => (
              <tr key={i} data-kind={h.kind}>
                <td>{h.when}</td>
                <td>{h.label}</td>
                <td className="ui-num">{h.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {model.statement !== null ? (
          <p className="ui-seed ui-seed--paper" data-testid="lease-statement">
            {model.statement}
          </p>
        ) : null}
      </section>
    </div>
  )
}
